import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listApartmentChannels,
  upsertApartmentChannel,
  activateApartmentChannel,
  disableApartmentChannel,
  syncApartmentChannel,
  setApartmentChannelPriceAdjustment,
  listApartmentChannelEvents,
  listApartmentChannelEventsAll,
  acknowledgeChannelEvents,
  listChannelConnections,
  getChannelConnection,
  createChannelConnection,
  deleteChannelConnection,
  setChannelConnectionActive,
  testChannelConnection,
  getBookingComMappingDetails,
  listAirbnbConnectionListings,
  createAirbnbConnectionLink,
  updateAirbnbConnectionSettings,
} from '@/api/generated/sdk.gen';
import type {
  ApartmentChannelMapping,
  ChannelConnection,
  ChannelSyncEvent,
  AirbnbListing,
  MappingDetails,
  AirbnbConnectionLinkResponse,
  UpsertApartmentChannelRequest,
  SetPriceAdjustmentRequest,
  CreateChannelConnectionRequest,
  AirbnbConnectionLinkRequest,
  AirbnbChannelSettings,
} from '@/api/generated/types.gen';

export type {
  ApartmentChannelMapping,
  ChannelConnection,
  ChannelSyncEvent,
  AirbnbListing,
  MappingDetails,
  AirbnbConnectionLinkResponse,
};

// Provider codes match the OpenAPI enum + backend CHECK constraint.
export const PROVIDERS = ['booking_com', 'airbnb', 'booking_engine'] as const;
export type Provider = (typeof PROVIDERS)[number];

export const PROVIDER_LABELS: Record<Provider, string> = {
  booking_com: 'Booking.com',
  airbnb: 'Airbnb',
  booking_engine: 'Direct / Booking Engine',
};

// Providers whose card carries its own sync-error list. Sync events come from
// the channel manager's live feed for an OTA channel, and connections are
// restricted to booking_com/airbnb (`channel_connections_provider_known`), so
// a booking_engine events query can only ever return [] — don't issue it.
//
// `channel_sync_events.provider` nevertheless PERMITS booking_engine, so a row
// outside this set is storable. Such a row is not silently dropped: it falls
// through to the apartment-wide "Other sync errors" block, which shows
// everything no card above accounts for.
export const SYNC_EVENT_PROVIDERS: readonly Provider[] = ['booking_com', 'airbnb'];

export const hasProviderSyncErrors = (p: Provider | null | undefined): boolean =>
  p != null && SYNC_EVENT_PROVIDERS.includes(p);

// ---------------------------------------------------------------------------
// Retry helper shared by all queries in this lib
// ---------------------------------------------------------------------------
const retryFn = (failureCount: number, err: unknown) => {
  const status = (err as { status?: number })?.status;
  return status !== 401 && status !== 404 && failureCount < 1;
};

// ---------------------------------------------------------------------------
// Apartment-scoped channel queries
// ---------------------------------------------------------------------------

// apartmentChannelsQuery returns the WHOLE list response (not just
// `.items`) — the response also carries `active_error_total` (every active
// channel_sync_events row for the apartment, attributed or not), which is
// what the Channels tab badge and the "Other sync errors" notice read.
export const apartmentChannelsQuery = (apartmentID: string) =>
  queryOptions({
    queryKey: ['apartments', apartmentID, 'channels'],
    queryFn: async ({ signal }) => {
      const res = await listApartmentChannels({
        throwOnError: true,
        signal,
        path: { id: apartmentID },
      });
      return res.data;
    },
    retry: retryFn,
    staleTime: 30_000,
  });

export const apartmentChannelEventsQuery = (
  apartmentID: string,
  provider: Provider,
) =>
  queryOptions({
    queryKey: ['apartments', apartmentID, 'channels', provider, 'events'],
    queryFn: async ({ signal }) => {
      const res = await listApartmentChannelEvents({
        throwOnError: true,
        signal,
        path: { id: apartmentID, provider },
      });
      return res.data.items;
    },
    retry: retryFn,
    staleTime: 30_000,
  });

// allChannelEventsKey is deliberately NOT a suffix of apartmentChannelsQuery's
// ['apartments', apartmentID, 'channels'] key (it reads 'channel-events', not
// 'channels' + 'events') — see useAcknowledgeChannelEvents below, which relies
// on the channels key staying a distinct, exact-only invalidation target.
const allChannelEventsKey = (apartmentID: string) => [
  'apartments',
  apartmentID,
  'channel-events',
];

// apartmentAllChannelEventsQuery — GET /apartments/{id}/channel-events (Task 14).
// Unlike apartmentChannelEventsQuery, this is not provider-scoped: it is the
// only read path that returns channel_sync_events rows with a NULL provider,
// which is what lets the "Other sync errors" block render real rows instead
// of a bare count.
export const apartmentAllChannelEventsQuery = (apartmentID: string) =>
  queryOptions({
    queryKey: allChannelEventsKey(apartmentID),
    queryFn: async ({ signal }) => {
      const res = await listApartmentChannelEventsAll({
        throwOnError: true,
        signal,
        path: { id: apartmentID },
      });
      return res.data.items;
    },
    retry: retryFn,
    staleTime: 30_000,
  });

// ---------------------------------------------------------------------------
// Channel-connection queries
// ---------------------------------------------------------------------------

export const channelConnectionsQuery = queryOptions({
  queryKey: ['channel-connections'],
  queryFn: async ({ signal }) => {
    const res = await listChannelConnections({ throwOnError: true, signal });
    return res.data.items;
  },
  retry: retryFn,
  staleTime: 30_000,
});

export const channelConnectionsByProviderQuery = (provider: Provider) =>
  queryOptions({
    queryKey: ['channel-connections', { provider }],
    queryFn: async ({ signal }) => {
      const res = await listChannelConnections({
        throwOnError: true,
        signal,
        query: { provider },
      });
      return res.data.items;
    },
    retry: retryFn,
    staleTime: 30_000,
  });

export const airbnbListingsQuery = (connectionId: string) =>
  queryOptions({
    queryKey: ['channel-connections', connectionId, 'airbnb-listings'],
    queryFn: async ({ signal }) => {
      const res = await listAirbnbConnectionListings({
        throwOnError: true,
        signal,
        path: { id: connectionId },
      });
      return res.data.items;
    },
    retry: retryFn,
    staleTime: 60_000,
  });

export const channelConnectionQuery = (id: string) =>
  queryOptions({
    queryKey: ['channel-connections', id],
    queryFn: async ({ signal }) => {
      const res = await getChannelConnection({
        throwOnError: true,
        signal,
        path: { id },
      });
      return res.data;
    },
    retry: retryFn,
    staleTime: 30_000,
  });

export const bookingComMappingDetailsQuery = (connectionId: string) =>
  queryOptions({
    queryKey: ['channel-connections', connectionId, 'mapping-details'],
    queryFn: async ({ signal }) => {
      const res = await getBookingComMappingDetails({
        throwOnError: true,
        signal,
        path: { id: connectionId },
      });
      return res.data;
    },
    retry: retryFn,
    staleTime: 60_000,
  });

// ---------------------------------------------------------------------------
// Apartment-channel mutations
// ---------------------------------------------------------------------------

// Which caches a channel mutation must refresh is not uniform, so it is
// spelled out here once rather than reasoned about at each call site.
//
// ['apartments', id, 'channels'] is a PREFIX of every per-provider events key,
// so one non-exact invalidate of it refreshes the mapping list AND all the
// per-provider sync-error lists. That is everything a mutation which only
// touches mapping rows can affect.
const invalidateChannels = (
  qc: ReturnType<typeof useQueryClient>,
  apartmentID: string,
) => {
  void qc.invalidateQueries({ queryKey: ['apartments', apartmentID, 'channels'] });
};

// The apartment-wide ['apartments', id, 'channel-events'] key sits OUTSIDE
// that prefix on purpose (see allChannelEventsKey), so it is never swept by
// the above and needs naming explicitly. Only mutations that can change an
// event's lifecycle state do so — see invalidateChannelsAndEvents.
const invalidateChannelsAndEvents = (
  qc: ReturnType<typeof useQueryClient>,
  apartmentID: string,
) => {
  invalidateChannels(qc, apartmentID);
  void qc.invalidateQueries({ queryKey: allChannelEventsKey(apartmentID) });
};

export const useUpsertChannelMapping = (apartmentID: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      provider,
      body,
    }: {
      provider: Provider;
      body: UpsertApartmentChannelRequest;
    }) =>
      upsertApartmentChannel({
        throwOnError: true,
        path: { id: apartmentID, provider },
        body,
      }),
    onSuccess: () => {
      invalidateChannels(qc, apartmentID);
    },
  });
};

export const useActivateChannel = (apartmentID: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider: Provider) =>
      activateApartmentChannel({
        throwOnError: true,
        path: { id: apartmentID, provider },
      }),
    onSuccess: () => {
      invalidateChannels(qc, apartmentID);
    },
  });
};

export const useDisableChannel = (apartmentID: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider: Provider) =>
      disableApartmentChannel({
        throwOnError: true,
        path: { id: apartmentID, provider },
      }),
    onSuccess: () => {
      invalidateChannels(qc, apartmentID);
    },
  });
};

export const useSyncChannel = (apartmentID: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider: Provider) =>
      syncApartmentChannel({
        throwOnError: true,
        path: { id: apartmentID, provider },
      }),
    onSuccess: () => {
      // "Sync now" (channel.Service.SyncMapping) is the only mutation that
      // writes BOTH halves of the view's manual_sync evidence — sync_status
      // ='synced' AND last_synced_at — so it is the only one that can
      // deactivate an event, INCLUDING an unattributed one, which only the
      // apartment-wide list shows. Hence the extra key here.
      //
      // Careful with the neighbours: mapping-upsert also writes
      // sync_status='synced' (MapApartment hardcodes it), so "writes no sync
      // status" is NOT why they get away with the narrower invalidate. Two
      // other reasons are:
      //   * UpsertApartmentChannelMapping never touches `last_synced_at`, and
      //     manual_sync needs `last_synced_at > occurred_at` — so the write
      //     alone satisfies nothing;
      //   * the view joins that evidence per-provider (m.provider = e.provider),
      //     so even if it did, only the upserted provider's own events could
      //     flip — and those ARE refreshed, since the channels key is a prefix
      //     of the per-provider events key.
      // If either of those ever changes, this call has to move.
      //
      // That prefix relationship is also why the explicit per-provider
      // invalidate this used to make was redundant.
      invalidateChannelsAndEvents(qc, apartmentID);
    },
  });
};

// useAcknowledgeChannelEvents — POST /apartments/{id}/channel-events:acknowledge
// (Task 11 + Task 14). Called either from a provider-scoped SyncErrors card
// (`provider` given — the endpoint is apartment-scoped, but that card only
// cares about its own provider's cache) or from the apartment-wide "Other
// sync errors" block (`provider` omitted, since unattributed rows have no
// provider to filter by).
//
// The response is the refreshed apartment-wide event list (Task 10), capped
// at 50 rows ordered occurred_at DESC — a page, not a complete set. So this
// does three things, not one:
//   1. Write the provider-filtered subset (if `provider` given) AND the full
//      apartment-wide response into their respective caches immediately —
//      the optimistic update (Active errors count drops without waiting on a
//      round trip) — for BOTH surfaces, since either one may be mounted at
//      the same time as the other.
//   2. ALSO invalidate those same events queries so a refetch self-heals if
//      there are more than 50 events apartment-wide and the acknowledged row
//      happened to fall outside the response's page. This was a correction
//      from the Task 10 review: don't let correctness depend on the ack
//      response being complete.
// The channels-list query is invalidated too (`exact: true`, since it is a
// PREFIX of the per-provider events key — a non-exact invalidate would
// re-trigger that events refetch a second time) so `active_error_total` /
// `active_error_count` recompute for the badge and the other cards.
export const useAcknowledgeChannelEvents = (apartmentID: string, provider?: Provider) => {
  const qc = useQueryClient();
  const eventsKey = provider
    ? ['apartments', apartmentID, 'channels', provider, 'events']
    : undefined;
  const allEventsKey = allChannelEventsKey(apartmentID);
  const channelsKey = ['apartments', apartmentID, 'channels'];
  return useMutation({
    mutationFn: (eventIds: string[]) =>
      acknowledgeChannelEvents({
        throwOnError: true,
        path: { id: apartmentID },
        body: { event_ids: eventIds },
      }),
    onSuccess: (res) => {
      if (eventsKey) {
        qc.setQueryData(
          eventsKey,
          res.data.items.filter((e) => e.provider === provider),
        );
      }
      qc.setQueryData(allEventsKey, res.data.items);
      if (eventsKey) void qc.invalidateQueries({ queryKey: eventsKey });
      void qc.invalidateQueries({ queryKey: allEventsKey });
      void qc.invalidateQueries({ queryKey: channelsKey, exact: true });
    },
  });
};

export const useSetPriceAdjustment = (apartmentID: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      provider,
      body,
    }: {
      provider: Provider;
      body: SetPriceAdjustmentRequest;
    }) =>
      setApartmentChannelPriceAdjustment({
        throwOnError: true,
        path: { id: apartmentID, provider },
        body,
      }),
    onSuccess: () => {
      invalidateChannels(qc, apartmentID);
    },
  });
};

// ---------------------------------------------------------------------------
// Channel-connection mutations
// ---------------------------------------------------------------------------

export const useCreateChannelConnection = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateChannelConnectionRequest) =>
      createChannelConnection({ throwOnError: true, body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channel-connections'] });
    },
  });
};

export const useDeleteChannelConnection = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      deleteChannelConnection({ throwOnError: true, path: { id } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channel-connections'] });
    },
  });
};

export const useSetConnectionActive = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      setChannelConnectionActive({ throwOnError: true, path: { id }, body: { active } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channel-connections'] });
    },
  });
};

export const useTestConnection = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      testChannelConnection({ throwOnError: true, path: { id } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channel-connections'] });
    },
  });
};

export const useCreateAirbnbConnectionLink = () =>
  useMutation({
    mutationFn: (body: AirbnbConnectionLinkRequest) =>
      createAirbnbConnectionLink({ throwOnError: true, body }),
    // No cache invalidation needed — this just returns a redirect URL;
    // the connection row appears after the OAuth callback completes.
  });

export const useUpdateAirbnbConnectionSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AirbnbChannelSettings }) =>
      updateAirbnbConnectionSettings({ throwOnError: true, path: { id }, body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channel-connections'] });
    },
  });
};
